// BestRx Command Center — Mission Control.
// Dark interface, real-time lead feed, scoring, reporting, multi-brand, call queue, revenue, pipeline.
import { useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Clock,
  DollarSign,
  Flame,
  Layers,
  Link2,
  LogOut,
  MapPin,
  Megaphone,
  Package,
  MessageSquare,
  Phone,
  Pill,
  Plug,
  Radio,
  Send,
  TrendingUp,
  Users,
  Webhook,
  X,
  Zap,
} from "lucide-react";

import { LeadDetailDrawer } from "../components/admin/LeadDetailDrawer";
import PipelineBoard from "../components/admin/PipelineBoard";
import { CallQueue as CallQueueV2 } from "../components/admin/CallQueue";
import { RevenueBreakdown } from "../components/admin/RevenueBreakdown";
import { BrandComparison } from "../components/admin/BrandComparison";
import { LeadFeed } from "../components/admin/LeadFeed";
import { KPICards } from "../components/admin/KPICards";
import { TexasHeatMap } from "../components/admin/TexasHeatMap";
import { CommsHub } from "../components/admin/CommsHub";
import { TickerManager } from "../components/admin/TickerManager";
import { CatalogManager } from "../components/admin/CatalogManager";
import { BundleManager } from "../components/admin/BundleManager";
import { leadAge as ageOf } from "../components/admin/shared";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const RED = "#FF2A3C";
const NEON = "#42FFB0";
const AMBER = "#FFB400";

const BRANDS = [
  { slug: "ALL",    label: "ALL BRANDS",   accent: "#FFFFFF" },
  { slug: "BestRx", label: "BESTRX",       accent: RED },
  { slug: "LeenRx", label: "LEENRX",       accent: "#7C5CFF" },
  { slug: "HappiRx",label: "HAPPIRX",      accent: "#42FFB0" },
  { slug: "Future", label: "+ NEW BRAND",  accent: "#6B7280" },
];

const TX_CITIES = [
  // Approximate normalized 0-1 coords on a Texas SVG (x,y in viewBox 0 0 100 100)
  { slug: "houston",     name: "Houston",      x: 72, y: 70 },
  { slug: "dallas",      name: "Dallas",       x: 62, y: 38 },
  { slug: "austin",      name: "Austin",       x: 55, y: 64 },
  { slug: "fort-worth",  name: "Fort Worth",   x: 58, y: 38 },
  { slug: "plano",       name: "Plano",        x: 63, y: 35 },
  { slug: "frisco",      name: "Frisco",       x: 62, y: 33 },
  { slug: "mckinney",    name: "McKinney",     x: 64, y: 32 },
  { slug: "allen",       name: "Allen",        x: 64, y: 34 },
  { slug: "rockwall",    name: "Rockwall",     x: 66, y: 38 },
  { slug: "garland",     name: "Garland",      x: 64, y: 37 },
  { slug: "denton",      name: "Denton",       x: 60, y: 32 },
  { slug: "lewisville",  name: "Lewisville",   x: 61, y: 34 },
];

const TABS = [
  { key: "feed",     label: "Live Feed",   Icon: Radio },
  { key: "leads",    label: "Lead Scoring",Icon: Flame },
  { key: "heatmap",  label: "TX Heat Map", Icon: MapPin },
  { key: "cities",   label: "Cities",      Icon: Building2 },
  { key: "meds",     label: "Medications", Icon: Pill },
  { key: "catalog",  label: "Catalog CMS", Icon: Pill },
  { key: "bundles",  label: "Bundles",     Icon: Package },
  { key: "brands",   label: "Brand Perf",  Icon: BarChart3 },
  { key: "pipeline", label: "Pipeline",    Icon: Layers },
  { key: "queue",    label: "Call Queue",  Icon: Phone },
  { key: "comms",    label: "Comms",       Icon: MessageSquare },
  { key: "ticker",   label: "Ticker",      Icon: Megaphone },
  { key: "revenue",  label: "Revenue",     Icon: DollarSign },
  { key: "webhooks", label: "Webhooks",    Icon: Webhook },
  { key: "adapters", label: "Adapters",    Icon: Plug },
];

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

/* ─────────  TOP BAR  ───────── */
function TopBar({ brand, setBrand, health, lastTick, user, onLogout }) {
  return (
    <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-30">
      <div className="px-5 lg:px-8 py-3.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="font-heavy text-white leading-none" style={{ fontSize: "22px" }}>
            BEST<span style={{ color: RED }}>RX</span>
            <span className="ml-2 text-[10px] font-extrabold tracking-[0.22em] text-white/55">COMMAND CENTER</span>
          </div>
          <div className="hidden md:flex items-center gap-1.5 ml-4 px-2.5 py-1 rounded-sm border border-[#42FFB0]/30 bg-[#42FFB0]/5">
            <CircleDot size={10} color={NEON} className="animate-pulse" />
            <span className="text-[10px] tracking-[0.18em] font-bold" style={{ color: NEON }}>LIVE · {lastTick}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-3 text-[11px] font-bold text-white/60">
            <span className="inline-flex items-center gap-1.5"><Plug size={12} /> {health?.adapters || 0} adapters</span>
            <span className="inline-flex items-center gap-1.5"><Webhook size={12} /> {health?.events?.length || 0} events</span>
          </div>
          <div className="relative">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              data-testid="brand-switch"
              className="appearance-none bg-black border border-white/15 text-white font-extrabold text-[12px] tracking-[0.14em] pl-3 pr-8 py-2 rounded-sm cursor-pointer focus:outline-none focus:border-[#FF2A3C]"
            >
              {BRANDS.map((b) => <option key={b.slug} value={b.slug}>{b.label}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 pointer-events-none" />
          </div>
          {user && (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex flex-col items-end leading-tight" data-testid="user-chip">
                <span className="text-[11px] font-extrabold text-white">{user.name || user.email}</span>
                <span className="text-[9.5px] tracking-[0.16em] font-bold" style={{ color: RED }}>{(user.role || "").toUpperCase()}</span>
              </div>
              <button onClick={onLogout} data-testid="logout-btn" title="Sign out"
                className="w-9 h-9 flex items-center justify-center rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-[#FF2A3C]">
                <LogOut size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─────────  TABS NAV  ───────── */
function TabNav({ tab, setTab }) {
  return (
    <nav className="flex items-center gap-1 mb-5 overflow-x-auto scrollbar-none" data-testid="tabs">
      {TABS.map(({ key, label, Icon }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-[11.5px] font-extrabold tracking-[0.14em] uppercase whitespace-nowrap rounded-sm transition-colors"
            style={{
              background: active ? RED : "transparent",
              color: active ? "white" : "rgba(255,255,255,0.65)",
              border: active ? `1px solid ${RED}` : "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Icon size={13} /> {label}
          </button>
        );
      })}
    </nav>
  );
}

/* ─────────  LEAD SCORING TABLE  ───────── */
function LeadScoring({ leads, onPickLead, cityFilter, onClearCityFilter }) {
  const filtered = useMemo(() => {
    if (!cityFilter) return leads;
    return leads.filter((l) => (l.city || "").toLowerCase() === cityFilter.toLowerCase());
  }, [leads, cityFilter]);
  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid="lead-scoring">
      <div className="px-5 py-3 border-b border-white/10 flex flex-col md:flex-row md:justify-between md:items-center gap-2">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Flame size={14} color={RED} /> LEAD SCORING — TOP {filtered.length}</p>
        <div className="inline-flex items-center gap-2">
          {cityFilter && (
            <button
              onClick={onClearCityFilter}
              data-testid="city-filter-dismiss"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase rounded-sm border"
              style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.08)" }}
            >
              <span data-testid="city-filter-chip">CITY: {cityFilter}</span> <X size={10} />
            </button>
          )}
          <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">Tier × Interest × Contactability</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] min-w-[760px]">
          <thead className="text-[10px] tracking-[0.16em] text-white/45 uppercase">
            <tr className="border-b border-white/5">
              <th className="text-left px-5 py-2.5">Score</th>
              <th className="text-left px-3 py-2.5">Age</th>
              <th className="text-left px-3 py-2.5">Name</th>
              <th className="text-left px-3 py-2.5">Email</th>
              <th className="text-left px-3 py-2.5">Phone</th>
              <th className="text-left px-3 py-2.5">City</th>
              <th className="text-left px-3 py-2.5">Interest</th>
              <th className="text-left px-3 py-2.5">Brand</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((l) => {
              const heat = l.score >= 80 ? "#FF2A3C" : l.score >= 60 ? "#FFB400" : "#5C8DFF";
              const age = ageOf(l.created_at);
              return (
                <tr key={l.id} onClick={() => onPickLead && onPickLead(l.id)} className="hover:bg-white/[0.04] cursor-pointer" data-testid={`lead-row-${l.id.slice(0,8)}`}>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-2 font-heavy text-white" style={{ color: heat, fontSize: "15px" }}>
                      <CircleDot size={8} className="animate-pulse" />
                      {l.score}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.10em]"
                      style={{ borderColor: age.color, color: age.color, background: `${age.color}10` }}
                      title={`Age: ${age.text}`}
                      data-testid={`lead-age-chip-${l.id.slice(0,8)}`}
                    >
                      <Clock size={9} /> {age.text}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-white">{[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-3 py-3 text-white/70 font-mono">{l.email}</td>
                  <td className="px-3 py-3 text-white/70 font-mono">{l.phone}</td>
                  <td className="px-3 py-3 text-white/70">{l.city || "—"}</td>
                  <td className="px-3 py-3 text-white/70">{l.interest || "—"}</td>
                  <td className="px-3 py-3"><span className="text-[10px] font-bold tracking-[0.12em] px-2 py-0.5 rounded-sm border border-white/15">{l.brand || "BestRx"}</span></td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-white/45">No leads {cityFilter ? `for ${cityFilter}` : "yet"}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────  HORIZONTAL BAR REPORT  ───────── */
function BarReport({ title, Icon, data, labelKey, valueKey }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  return (
    <div className="rounded-sm border border-white/10 bg-black/50">
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Icon size={14} color={RED} /> {title}</p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">{data.length} rows</span>
      </div>
      <div className="p-5 space-y-2.5">
        {data.length === 0 && <p className="text-white/45 text-[13px] text-center py-6">No data yet.</p>}
        {data.map((row) => {
          const pct = (row[valueKey] / max) * 100;
          return (
            <div key={row[labelKey]} className="space-y-1">
              <div className="flex items-center justify-between text-[11.5px]">
                <span className="font-extrabold text-white tracking-tight">{row[labelKey]}</span>
                <span className="font-heavy text-white" style={{ color: RED }}>{row[valueKey]}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-sm overflow-hidden">
                <div className="h-full rounded-sm transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${RED} 0%, #FF6B7E 100%)` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────  PIPELINE (Kanban)  ───────── */
function Pipeline({ leads, assessments, reviews, shipments, onPickLead }) {
  const cols = [
    { k: "lead",        t: "LEADS",        count: leads.length,       Icon: Users,        accent: RED,    rows: leads.slice(0, 8).map(l => ({ id: l.id, label: l.email || l.phone, sub: l.interest, clickable: true })) },
    { k: "assessed",    t: "ASSESSED",     count: assessments.length, Icon: CheckCircle2, accent: "#5C8DFF", rows: assessments.slice(0, 8).map(l => ({ id: l.id, label: l.email, sub: l.interest, clickable: true })) },
    { k: "approved",    t: "PROVIDER APPROVED", count: reviews.length, Icon: Plug,         accent: NEON,   rows: reviews.slice(0, 8).map(l => ({ id: l.id, label: l.lead_id?.slice(0,12), sub: l.decision, lead_id: l.lead_id, clickable: !!l.lead_id })) },
    { k: "shipped",     t: "SHIPPED",      count: shipments.length,   Icon: Send,         accent: "#9B5BFF", rows: shipments.slice(0, 8).map(l => ({ id: l.id, label: l.tracking_number, sub: l.carrier })) },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline">
      {cols.map(({ k, t, count, Icon, accent, rows }) => (
        <div key={k} className="rounded-sm border border-white/10 bg-black/50" data-testid={`pipeline-${k}`}>
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <p className="font-heavy text-[12px] tracking-tight inline-flex items-center gap-2" style={{ color: accent }}>
              <Icon size={12} /> {t}
            </p>
            <span className="font-heavy" style={{ color: accent, fontSize: "16px" }}>{count}</span>
          </div>
          <div className="divide-y divide-white/5">
            {rows.length === 0 && <p className="px-4 py-8 text-center text-white/35 text-[12px]">empty</p>}
            {rows.map((r) => {
              const targetId = r.lead_id || r.id;
              const onClick = r.clickable && onPickLead ? () => onPickLead(targetId) : undefined;
              return (
                <div
                  key={r.id}
                  onClick={onClick}
                  data-testid={`pipeline-card-${k}-${r.id.slice(0, 8)}`}
                  className={`px-4 py-2.5 ${onClick ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
                >
                  <p className="text-[12px] font-mono text-white truncate">{r.label || "—"}</p>
                  {r.sub && <p className="text-[10.5px] text-white/45 tracking-tight mt-0.5">{r.sub}</p>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────  WEBHOOK DELIVERIES  ───────── */
function WebhookDeliveries({ deliveries }) {
  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid="webhook-deliveries">
      <div className="px-5 py-3 border-b border-white/10 flex justify-between items-center">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Webhook size={14} color={RED} /> WEBHOOK DELIVERIES</p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">{deliveries.length} attempts</span>
      </div>
      <div className="divide-y divide-white/5 max-h-[520px] overflow-y-auto">
        {deliveries.length === 0 && <p className="px-5 py-10 text-center text-white/45 text-[13px]">No webhook deliveries yet.</p>}
        {deliveries.map((d) => {
          const ok = d.status >= 200 && d.status < 300;
          return (
            <div key={d.id} className="grid grid-cols-12 gap-3 px-5 py-3 hover:bg-white/[0.02]" data-testid={`delivery-${d.id.slice(0,8)}`}>
              <div className="col-span-2"><span className="font-heavy text-[11px]" style={{ color: ok ? NEON : RED }}>{ok ? "DELIVERED" : "FAILED"} {d.status}</span></div>
              <div className="col-span-2 font-heavy text-[11px] tracking-tight text-white">{d.event}</div>
              <div className="col-span-5 text-[11px] text-white/55 font-mono truncate">{d.url}</div>
              <div className="col-span-1 text-[11px] text-white/55">×{d.attempts}</div>
              <div className="col-span-2 text-right text-[11px] text-white/45 font-mono">{ago(d.created_at)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────  ADAPTERS PANEL  ───────── */
function AdaptersPanel({ adapters, integrations, runs, refresh }) {
  const byKey = Object.fromEntries(integrations.map((i) => [i.provider, i]));
  const cats = [...new Set(adapters.map((a) => a.category))];

  async function toggle(provider, enabled, currentCfg = {}) {
    await fetch(`${API}/integrations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider, enabled, config: currentCfg }),
    });
    refresh();
  }

  return (
    <div className="space-y-4" data-testid="adapters-panel">
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Plug size={14} color={RED} /> VENDOR ADAPTERS — {adapters.length} AVAILABLE</p>
          <span className="text-[10px] tracking-[0.16em] font-bold" style={{ color: NEON }}>{integrations.filter((i) => i.enabled).length} ENABLED</span>
        </div>
        <div className="p-5 space-y-5">
          {cats.map((cat) => (
            <div key={cat}>
              <p className="font-heavy text-[10.5px] tracking-[0.2em] text-white/55 mb-2">{cat.toUpperCase()}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {adapters.filter((a) => a.category === cat).map((a) => {
                  const cfg = byKey[a.key];
                  const on = !!cfg?.enabled;
                  return (
                    <div key={a.key} className="border border-white/10 rounded-sm p-4 bg-black/30" data-testid={`adapter-${a.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-heavy text-[13px] text-white">{a.name}</p>
                        <button
                          onClick={() => toggle(a.key, !on, cfg?.config || {})}
                          className="text-[10px] font-extrabold tracking-[0.14em] px-2 py-1 rounded-sm"
                          style={{ background: on ? NEON : "transparent", color: on ? "black" : "white", border: `1px solid ${on ? NEON : "rgba(255,255,255,0.2)"}` }}
                        >
                          {on ? "ENABLED" : "DISABLED"}
                        </button>
                      </div>
                      <p className="text-[10.5px] text-white/55">{a.events.length} event{a.events.length !== 1 ? "s" : ""}</p>
                      {cfg && (
                        <p className="text-[10px] mt-1 font-mono text-white/45">cfg: {Object.keys(cfg.config || {}).join(", ") || "—"}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {runs.length > 0 && (
        <div className="rounded-sm border border-white/10 bg-black/50">
          <div className="px-5 py-3 border-b border-white/10"><p className="font-heavy text-[14px] inline-flex items-center gap-2"><Zap size={14} color={NEON} /> RECENT ADAPTER CALLS</p></div>
          <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
            {runs.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-3 px-5 py-2.5 text-[11.5px]">
                <span className="col-span-2 font-heavy" style={{ color: r.ok ? NEON : RED }}>{r.ok ? "OK" : "ERR"}</span>
                <span className="col-span-2 font-extrabold text-white">{r.provider}</span>
                <span className="col-span-3 text-white/70">{r.event}</span>
                <span className="col-span-3 font-mono text-white/45 truncate">{JSON.stringify(r.result).slice(0, 80)}</span>
                <span className="col-span-2 text-right text-white/45">{ago(r.started_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────  DEMO LAB  ───────── */
function DemoLab({ refresh }) {
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState([]);
  const log = (label, ok, detail) =>
    setResults((p) => [{ ts: new Date().toISOString(), label, ok, detail }, ...p].slice(0, 8));

  const fire = useCallback(async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      log(label, r.ok !== false, JSON.stringify(r).slice(0, 220));
    } catch (e) {
      log(label, false, e.message);
    }
    setBusy(null);
    refresh();
  }, [refresh]);

  const post = (path, body) => fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then((r) => r.json());

  const demos = [
    { k: "lead",     label: "Create Lead",        fn: () => post("/leads",     { first_name: "Demo", last_name: "Lead",  email: `demo${Date.now()}@bestrx.online`, phone: "+12148155966", interest: "Tirzepatide", city: "Dallas", brand: "BestRx" }) },
    { k: "callback", label: "Request Callback",   fn: () => post("/call-requests", { first_name: "Demo Caller", phone: "+12148155966" }) },
    { k: "asmt",     label: "Assessment Done",    fn: () => post("/assessments",   { email: `demo${Date.now()}@bestrx.online`, phone: "+12148155966", interest: "Semaglutide", answers: { weight_lbs: 220, age: 38 } }) },
    { k: "pay",      label: "Payment Received",   fn: () => post("/payments",      { email: `demo${Date.now()}@bestrx.online`, amount_cents: 3500, processor: "stripe", description: "$35 week 1" }) },
    { k: "ship",     label: "Medication Shipped", fn: () => post("/shipments",     { lead_id: "demo-lead", carrier: "FedEx", tracking_number: `TRK${Date.now()}`, medications: ["tirzepatide"] }) },
    { k: "wh",       label: "Subscribe Webhook",  fn: () => post("/webhooks",      { url: "https://webhook.site/test-bestrx", events: ["*"], description: "Smoke test" }) },
  ];

  return (
    <div className="rounded-sm border border-white/10 bg-black/50 mb-5" data-testid="demo-lab">
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Zap size={14} color={AMBER} /> DEMO LAB — FIRE EVENTS ON DEMAND</p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">All actions emit events + fan out</span>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {demos.map(({ k, label, fn }) => (
            <button
              key={k}
              onClick={() => fire(label, fn)}
              disabled={busy === label}
              data-testid={`demo-${k}`}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] font-extrabold tracking-[0.08em] uppercase rounded-sm border"
              style={{ background: busy === label ? "rgba(255,42,60,0.2)" : "transparent", borderColor: RED, color: "white" }}
            >
              {busy === label ? "FIRING…" : label} <ArrowUpRight size={12} />
            </button>
          ))}
        </div>
        {results.length > 0 && (
          <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
            {results.map((r, i) => (
              <div key={i} className="text-[11px] font-mono px-3 py-2 rounded-sm border" style={{ borderColor: r.ok ? "rgba(66,255,176,0.3)" : "rgba(255,42,60,0.3)", background: r.ok ? "rgba(66,255,176,0.04)" : "rgba(255,42,60,0.04)" }}>
                <span style={{ color: r.ok ? NEON : RED }} className="font-heavy">{r.ok ? "✓" : "✗"}</span>
                <span className="text-white/85 ml-2">{r.label}</span>
                <span className="text-white/45 ml-2">{r.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────  AUTH GATE  ───────── */
function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Session lives in an HttpOnly cookie (auto-sent same-origin). We never read
  // or store the JWT in JS/localStorage — we just ask the server who we are.
  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setUser(u || null))
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pw }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const d = body?.detail;
        const msg = typeof d === "string" ? d
          : Array.isArray(d) ? (d[0]?.msg || "Login failed")
          : (d?.msg || "Login failed");
        throw new Error(msg);
      }
      const data = await r.json();
      setUser(data.user);
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  const logout = async () => {
    try { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); } catch (e) { /* ignore */ }
    setUser(null);
  };

  if (!user) {
    if (!checked) {
      return <div className="min-h-screen bg-[#06070A]" data-testid="auth-checking" />;
    }
    return (
      <div className="min-h-screen bg-[#06070A] flex items-center justify-center px-4" data-testid="auth-gate">
        <div className="w-full max-w-[420px]">
          <div className="text-center mb-8">
            <div className="font-heavy text-white leading-none mx-auto" style={{ fontSize: "44px" }}>
              BEST<span style={{ color: RED }}>RX</span>
            </div>
            <p className="mt-3 text-[10px] font-extrabold tracking-[0.22em] text-white/55">COMMAND CENTER · SECURE ACCESS</p>
          </div>
          <form onSubmit={login} className="rounded-sm border border-white/10 bg-black/60 p-6 space-y-3 backdrop-blur" data-testid="login-form">
            <label className="block">
              <span className="text-[10.5px] tracking-[0.16em] font-bold text-white/55">EMAIL</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email"
                className="mt-1.5 w-full bg-black border border-white/15 px-3 py-2.5 text-white font-mono text-[13px] focus:border-[#FF2A3C] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-[10.5px] tracking-[0.16em] font-bold text-white/55">PASSWORD</span>
              <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" required data-testid="login-password"
                className="mt-1.5 w-full bg-black border border-white/15 px-3 py-2.5 text-white font-mono text-[13px] focus:border-[#FF2A3C] focus:outline-none" />
            </label>
            {err && <p className="text-[12px] font-mono" data-testid="login-error" style={{ color: RED }}>{err}</p>}
            <button disabled={busy} type="submit" data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-extrabold tracking-[0.12em] uppercase text-white text-[12.5px]"
              style={{ background: RED, boxShadow: "0 12px 28px rgba(255,42,60,0.30)" }}>
              {busy ? "Authenticating…" : "Enter Command Center"} <ArrowUpRight size={14} strokeWidth={2.6} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  // token is null — auth now rides on the HttpOnly cookie (auto-sent same-origin).
  return children({ user, token: null, logout });
}

export default function CommandCenter() {
  return (
    <>
      {/* Admin app — never index */}
      <Helmet>
        <title>BestRx Mission Control</title>
        <meta name="robots" content="noindex,nofollow,noarchive,nosnippet" />
      </Helmet>
      <AuthGate>
        {({ user, token, logout }) => <CommandCenterInner user={user} token={token} onLogout={logout} />}
      </AuthGate>
    </>
  );
}

function CommandCenterInner({ user, token, onLogout }) {
  const [tab, setTab] = useState("feed");
  const [brand, setBrand] = useState("ALL");
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [cities, setCities] = useState([]);
  const [meds, setMeds] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [runs, setRuns] = useState([]);
  const [health, setHealth] = useState(null);
  const [lastTick, setLastTick] = useState(new Date().toLocaleTimeString());

  // Lead detail drawer + global city filter (set by Texas Heat Map clicks)
  const [openLeadId, setOpenLeadId] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);

  const fetchJSON = useCallback((p) => fetch(`${API}${p}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.ok ? r.json() : []).catch(() => []), [token]);

  const refreshAll = useCallback(async () => {
    // Build query string combining brand + global cityFilter
    const params = new URLSearchParams();
    if (brand && brand !== "ALL" && brand !== "Future") params.set("brand", brand);
    if (cityFilter) params.set("city", cityFilter);
    const leadsQ = params.toString() ? `?${params.toString()}` : "";
    const [ov, ev, ls, py, asm, rv, sh, ct, md, ad, ig, dv, rn, hl] = await Promise.all([
      fetchJSON(`/reports/overview`),
      fetchJSON(`/events?limit=50`),
      fetchJSON(`/leads/scored${leadsQ}`),
      fetchJSON(`/payments`),
      fetchJSON(`/assessments`),
      fetchJSON(`/provider-reviews`),
      fetchJSON(`/shipments`),
      fetchJSON(`/reports/cities`),
      fetchJSON(`/reports/medications`),
      fetchJSON(`/integrations/available`),
      fetchJSON(`/integrations`),
      fetchJSON(`/webhooks/deliveries?limit=50`),
      fetchJSON(`/integrations/runs?limit=30`),
      fetchJSON(`/platform/health`),
    ]);
    setOverview(ov); setEvents(ev); setLeads(ls); setPayments(py);
    setAssessments(asm); setReviews(rv); setShipments(sh);
    setCities(ct); setMeds(md);
    setAdapters(ad?.adapters || []); setIntegrations(ig); setDeliveries(dv); setRuns(rn);
    setHealth(hl);
    setLastTick(new Date().toLocaleTimeString());
  }, [brand, cityFilter, fetchJSON]);

  useEffect(() => {
    refreshAll();
    const id = setInterval(refreshAll, 3000);
    return () => clearInterval(id);
  }, [refreshAll]);

  const pickLead = useCallback((id) => setOpenLeadId(id), []);
  const handleHeatPick = useCallback((cityName) => {
    // Toggle off if clicking the same city again
    setCityFilter((curr) => (curr && curr.toLowerCase() === cityName.toLowerCase() ? null : cityName));
  }, []);
  const clearCity = useCallback(() => setCityFilter(null), []);

  return (
    <div className="min-h-screen bg-[#06070A] text-white overflow-x-hidden" data-testid="command-center">
      <TopBar brand={brand} setBrand={setBrand} health={health} lastTick={lastTick} user={user} onLogout={onLogout} />
      <div className="px-3 sm:px-5 lg:px-8 py-4 lg:py-5 pb-24">
        <KPICards overview={overview} />
        {cityFilter && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-sm border" data-testid="global-city-banner"
            style={{ borderColor: RED, background: "rgba(255,42,60,0.08)" }}>
            <span className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-white inline-flex items-center gap-2">
              <span style={{ color: RED }}>●</span>
              City filter active: <span style={{ color: RED }}>{cityFilter.toUpperCase()}</span>
              <span className="text-white/55 font-bold normal-case tracking-normal hidden sm:inline">
                — affecting Lead Scoring, Call Queue, Revenue, Brand Comparison
              </span>
            </span>
            <button onClick={clearCity} data-testid="global-city-clear"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase rounded-sm border"
              style={{ borderColor: RED, color: RED }}>
              Clear <X size={10} />
            </button>
          </div>
        )}
        <DemoLab refresh={refreshAll} />
        <TabNav tab={tab} setTab={setTab} />
        {tab === "feed"     && <LeadFeed events={events} onPickLead={pickLead} />}
        {tab === "leads"    && <LeadScoring leads={leads} onPickLead={pickLead} cityFilter={cityFilter} onClearCityFilter={clearCity} />}
        {tab === "heatmap"  && <TexasHeatMap cities={cities} activeCity={cityFilter} onPick={handleHeatPick} />}
        {tab === "cities"   && <BarReport title="LEADS BY CITY"       Icon={MapPin}    data={cities} labelKey="city"       valueKey="leads" />}
        {tab === "meds"     && <BarReport title="LEADS BY MEDICATION" Icon={Pill}      data={meds}   labelKey="medication" valueKey="leads" />}
        {tab === "catalog"  && <CatalogManager token={token} />}
        {tab === "bundles"  && <BundleManager token={token} />}
        {tab === "brands"   && <BrandComparison token={token} cityFilter={cityFilter} />}
        {tab === "pipeline" && <PipelineBoard onPickLead={pickLead} />}
        {tab === "queue"    && <CallQueueV2 token={token} onPickLead={pickLead} cityFilter={cityFilter} />}
        {tab === "comms"    && <CommsHub token={token} />}
        {tab === "ticker"   && <TickerManager token={token} />}
        {tab === "revenue"  && <RevenueBreakdown token={token} payments={payments} onSeeded={refreshAll} cityFilter={cityFilter} />}
        {tab === "webhooks" && <WebhookDeliveries deliveries={deliveries} />}
        {tab === "adapters" && <AdaptersPanel adapters={adapters} integrations={integrations} runs={runs} refresh={refreshAll} />}
      </div>

      {openLeadId && (
        <LeadDetailDrawer
          leadId={openLeadId}
          token={token}
          onClose={() => setOpenLeadId(null)}
          onChanged={refreshAll}
        />
      )}
    </div>
  );
}
